/**
 * THERO Therōpedia index - search.
 *
 * WHAT THIS FILE DELIBERATELY DOES NOT DO:
 *  - filter or sort the entries already in the page. That approach only ever sees ONE page of entries, so on a
 *    blog with more entries than fit a page it silently returns the wrong answer. Topics are real links to
 *    /blogs/<blog>/tagged/<handle>, which Shopify resolves against the whole archive.
 *  - page through the archive. Since 2026-10-08 the pages are plain numbered links (?page=N) printed by the
 *    section: each page has its own address, works without JavaScript, and is what search engines follow. The old
 *    "Load more" button lived here and is gone - it appended pages and then had to hide the pager it competed with.
 *
 * What is left is the one job that genuinely belongs in the browser: search as you type, through Shopify's
 * predictive endpoint (the whole archive). Results are labelled for the site tracker like the entry rows
 * (assets/thero-analytics.js reads data-track-*), and a search that settles is reported as GA4's own "search"
 * event with its search_term.
 *
 * And the Topic / Month filters' manners (2026-10-08): they are <details> with real links inside, so they work with
 * no script at all; this adds what a reader expects of a dropdown - one open at a time, a tap outside or Escape
 * closes it, and a running search closes it too.
 */
(function () {
  'use strict';

  var DEBOUNCE = 220;   // ms between the last keystroke and the request
  var SETTLE = 1200;    // ms a term must stay unchanged before it counts as a search for analytics
  var MIN_CHARS = 2;

  function esc(s) {
    return String(s == null ? '' : s);
  }

  // /blogs/theropedia/<handle>?... -> <handle> (the same id the section prints for the entry)
  function handleOf(url) {
    var path = String(url || '').split('?')[0].split('#')[0];
    var parts = path.split('/').filter(Boolean);
    return parts.length ? parts[parts.length - 1] : '';
  }

  function track(event, params) {
    try {
      document.dispatchEvent(new CustomEvent('thero:track', { detail: { event: event, params: params || {} } }));
    } catch (e) { /* analytics must never break the search */ }
  }

  function initFilters(root) {
    var filters = Array.prototype.slice.call(root.querySelectorAll('[data-tbi-filter]'));
    if (!filters.length) return;
    filters.forEach(function (d) {
      d.addEventListener('toggle', function () {
        if (!d.open) return;
        filters.forEach(function (o) { if (o !== d && o.open) o.open = false; });
      });
    });
    document.addEventListener('click', function (e) {
      filters.forEach(function (d) { if (d.open && !d.contains(e.target)) d.open = false; });
    });
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      filters.forEach(function (d) {
        if (!d.open) return;
        d.open = false;
        var s = d.querySelector('summary');
        if (s) s.focus();
      });
    });
  }

  function closeFilters(root) {
    root.querySelectorAll('[data-tbi-filter][open]').forEach(function (d) { d.open = false; });
  }

  function init(root) {
    if (!root || root.dataset.tbiReady === '1') return;
    root.dataset.tbiReady = '1';

    initFilters(root);

    var input = root.querySelector('[data-tbi-search-input]');
    if (!input) return;

    var clearBtn = root.querySelector('[data-tbi-search-clear]');
    var results = root.querySelector('[data-tbi-results]');
    var resList = root.querySelector('[data-tbi-reslist]');
    var resCount = root.querySelector('[data-tbi-rescount]');
    var limit = parseInt(root.dataset.searchLimit || '10', 10) || 10;
    var readCta = root.dataset.readCta || 'Read the entry';
    var blogPath = root.dataset.blogUrl ? root.dataset.blogUrl.replace(/\/?$/, '/') : '';

    // everything that belongs to browsing (the list heading, the filters, the entries, the
    // pager) carries data-tbi-browse in the section, and steps aside while a search is showing
    var browseParts = Array.prototype.slice.call(root.querySelectorAll('[data-tbi-browse]'));

    var timer = null;
    var settleTimer = null;
    var seq = 0;
    var lastSent = '';

    function showBrowse() {
      browseParts.forEach(function (el) { el.hidden = false; });
      if (results) results.hidden = true;
      if (resList) resList.innerHTML = '';
    }

    function showResults() {
      closeFilters(root);
      browseParts.forEach(function (el) { el.hidden = true; });
      if (results) results.hidden = false;
    }

    function rowFor(a, i) {
      // Built with DOM methods, not innerHTML: the title and url come back from Shopify carrying whatever the
      // writer typed, so it is never pasted in as markup.
      var art = document.createElement('article');
      art.className = 'tbi-row';
      var handle = handleOf(a.url);
      // the tracking contract (same labels as the section's rows; slot = position in the results)
      if (handle) {
        art.setAttribute('data-track-promo', 'blog_' + handle);
        art.setAttribute('data-track-dest', 'article:' + handle);
      }
      art.setAttribute('data-track-promo-name', esc(a.title));
      art.setAttribute('data-track-creative', 'search_row');
      art.setAttribute('data-track-slot', 'blog_search:' + (i + 1));
      art.setAttribute('data-track-kind', 'article');

      var hit = document.createElement('a');
      hit.className = 'tbi-hit';
      hit.href = a.url || '#';
      var lab = document.createElement('span');
      lab.className = 'tbi-hit-label';
      lab.textContent = readCta + ': ' + esc(a.title);
      hit.appendChild(lab);
      art.appendChild(hit);

      if (a.image) {
        var thumb = document.createElement('span');
        thumb.className = 'tbi-rthumb';
        var img = document.createElement('img');
        img.className = 'tbi-rimg';
        // the predictive endpoint answers with the ORIGINAL photo (2,500px+); Shopify's CDN resizes on request, and
        // the thumb draws the photo ~114px wide (64px square covering 16:9), so 240px is sharp on a 2x phone
        img.src = /\/cdn\/shop\/|cdn\.shopify\.com/.test(a.image)
          ? a.image + (a.image.indexOf('?') === -1 ? '?' : '&') + 'width=240'
          : a.image;
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        thumb.appendChild(img);
        art.appendChild(thumb);
      }

      var body = document.createElement('span');
      body.className = 'tbi-rbody';
      var title = document.createElement('span');
      title.className = 'tbi-rtitle';
      title.textContent = esc(a.title);
      body.appendChild(title);
      art.appendChild(body);

      var go = document.createElement('span');
      go.className = 'tbi-go';
      go.setAttribute('aria-hidden', 'true');
      // a constant icon authored here - no search term or Shopify content reaches this string
      go.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg>';
      art.appendChild(go);

      return art;
    }

    function render(articles, failed) {
      resList.innerHTML = '';
      if (!articles.length) {
        resCount.textContent = '';
        var none = document.createElement('p');
        none.className = 'tbi-nores';
        // a failed request (offline, a busy store) must not read as "nothing matches"
        none.textContent = failed
          ? (root.dataset.msgError || 'Search is not available right now. Please try again in a moment.')
          : (root.dataset.msgNone || 'Nothing matches that yet.');
        resList.appendChild(none);
        return;
      }
      var tpl = articles.length === 1
        ? (root.dataset.msgOne || '1 entry found')
        : (root.dataset.msgMany || '[n] entries found');
      resCount.textContent = tpl.replace('[n]', articles.length);
      var frag = document.createDocumentFragment();
      articles.forEach(function (a, i) { frag.appendChild(rowFor(a, i)); });
      resList.appendChild(frag);
      // Shopify's predictive search returns at most `limit` - say so, so a full list never reads as "that's all"
      if (articles.length >= limit && root.dataset.msgTop) {
        var note = document.createElement('p');
        note.className = 'tbi-resnote';
        note.textContent = root.dataset.msgTop;
        resList.appendChild(note);
      }
    }

    // One analytics "search" per term the reader actually settled on, not one per keystroke.
    function report(term) {
      clearTimeout(settleTimer);
      if (!term || term.length < MIN_CHARS || term === lastSent) return;
      lastSent = term;
      track('search', { search_term: term.toLowerCase().slice(0, 100) });
    }

    function run(term) {
      var mine = ++seq;
      var url = '/search/suggest.json?q=' + encodeURIComponent(term)
        + '&resources[type]=article&resources[limit]=' + limit
        + '&resources[options][unavailable_products]=last';

      resCount.textContent = root.dataset.msgWorking || 'Searching';
      showResults();

      fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)); })
        .then(function (data) {
          // a slower earlier request must never overwrite a newer one's answer
          if (mine !== seq) return;
          var arts = (((data || {}).resources || {}).results || {}).articles || [];
          // Therōpedia in several languages (2026-10-28): Shopify's search spans EVERY blog, and the translated
          // entries keep product names in English, so "vitamin c" would list the Hindi copies on the English page.
          // Keep only this blog's own entries.
          if (blogPath) {
            arts = arts.filter(function (a) { return String(a.url || '').indexOf(blogPath) === 0; });
          }
          render(arts);
          clearTimeout(settleTimer);
          settleTimer = setTimeout(function () { if (input.value.trim() === term) report(term); }, SETTLE);
        })
        .catch(function () {
          if (mine !== seq) return;
          render([], true);
        });
    }

    input.addEventListener('input', function () {
      var term = input.value.trim();
      if (clearBtn) clearBtn.hidden = term.length === 0;
      clearTimeout(timer);
      clearTimeout(settleTimer);
      if (term.length < MIN_CHARS) {
        seq++; // cancel anything in flight
        showBrowse();
        return;
      }
      timer = setTimeout(function () { run(term); }, DEBOUNCE);
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        // the keyboard's "search" key: report now, and close the phone keyboard over the results
        var term = input.value.trim();
        if (term.length >= MIN_CHARS) report(term);
        input.blur();
      } else if (e.key === 'Escape') {
        input.value = '';
        if (clearBtn) clearBtn.hidden = true;
        clearTimeout(timer);       // a search typed within the last 220 ms must not run after the clear
        clearTimeout(settleTimer);
        seq++;
        showBrowse();
      }
    });

    // a tap on a result counts the search too (it was useful enough to open)
    if (resList) {
      resList.addEventListener('click', function () { report(input.value.trim()); }, true);
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        input.value = '';
        clearBtn.hidden = true;
        clearTimeout(timer);       // a search typed within the last 220 ms must not run after the clear
        clearTimeout(settleTimer);
        seq++;
        showBrowse();
        input.focus();
      });
    }
  }

  function boot() {
    document.querySelectorAll('[data-tbi]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
